<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import DetectedCampaignsAPI from 'dashboard/api/detectedCampaigns';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');

const bursts = ref([]);
const isLoading = ref(true);
// The burst being recorded, or 'bulk' while the whole selection is: one action at a time, and the
// button that started it is the one that spins.
const pending = ref(null);
const selectedIds = ref(new Set());
// Optional, keyed by burst id. An empty one lets the server name the campaign after the send date.
const titles = ref({});
const selected = ref(null);
const recipients = ref([]);
const isLoadingRecipients = ref(false);
const dismissDialogRef = ref(null);
const recipientsDialogRef = ref(null);
const bulkFormalizeDialogRef = ref(null);
const bulkDismissDialogRef = ref(null);

const inboxName = inboxId =>
  inboxes.value.find(inbox => inbox.id === inboxId)?.name ?? '';

const formatDate = value => new Date(value).toLocaleString();

const duration = burst => {
  const minutes = Math.round(
    (new Date(burst.ended_at) - new Date(burst.started_at)) / 60000
  );
  return minutes < 1
    ? t('CAMPAIGN.DETECTED.DURATION_INSTANT')
    : t('CAMPAIGN.DETECTED.DURATION_MINUTES', { count: minutes });
};

const senderLabel = burst =>
  burst.senders?.length
    ? t('CAMPAIGN.DETECTED.SENT_BY', { names: burst.senders.join(', ') })
    : t('CAMPAIGN.DETECTED.SENT_BY_UNKNOWN');

const hasBursts = computed(() => bursts.value.length > 0);
const isBusy = computed(() => pending.value !== null);
const selectedCount = computed(() => selectedIds.value.size);
const allSelected = computed(
  () => hasBursts.value && selectedCount.value === bursts.value.length
);
const selectAllLabel = computed(() =>
  allSelected.value
    ? t('CAMPAIGN.DETECTED.BULK.UNSELECT_ALL')
    : t('CAMPAIGN.DETECTED.BULK.SELECT_ALL')
);

const isSelected = id => selectedIds.value.has(id);

const toggleSelection = id => {
  const updated = new Set(selectedIds.value);
  if (!updated.delete(id)) updated.add(id);
  selectedIds.value = updated;
};

const selectAll = () => {
  selectedIds.value = new Set(bursts.value.map(burst => burst.id));
};

const titleFor = id => (titles.value[id] ?? '').trim();

const selectionPayload = computed(() =>
  bursts.value
    .filter(burst => isSelected(burst.id))
    .map(burst => ({ id: burst.id, title: titleFor(burst.id) }))
);

const fetchBursts = async () => {
  isLoading.value = true;
  try {
    const { data } = await DetectedCampaignsAPI.get();
    bursts.value = data.payload;
    // A burst that left the list was recorded or dismissed, and a selection still holding it would
    // ask the server for it again on the next bulk action.
    const listed = new Set(bursts.value.map(burst => burst.id));
    selectedIds.value = new Set(
      [...selectedIds.value].filter(id => listed.has(id))
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchBursts();
});

const openDismiss = burst => {
  selected.value = burst;
  dismissDialogRef.value.open();
};

const openRecipients = async burst => {
  selected.value = burst;
  recipients.value = [];
  isLoadingRecipients.value = true;
  recipientsDialogRef.value.open();
  try {
    const { data } = await DetectedCampaignsAPI.show(burst.id);
    recipients.value = data.recipients;
  } finally {
    isLoadingRecipients.value = false;
  }
};

// The store holds the campaign lists the other tabs render; whatever was just recorded belongs
// there too.
const reloadAfterFormalize = async () => {
  store.dispatch('campaigns/get');
  await fetchBursts();
};

const formalize = async burst => {
  pending.value = burst.id;
  try {
    await DetectedCampaignsAPI.formalize(burst.id, titleFor(burst.id));
    useAlert(t('CAMPAIGN.DETECTED.CONFIRM.SUCCESS'));
    await reloadAfterFormalize();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ?? t('CAMPAIGN.DETECTED.CONFIRM.ERROR')
    );
  } finally {
    pending.value = null;
  }
};

const confirmBulkFormalize = async () => {
  bulkFormalizeDialogRef.value.close();
  pending.value = 'bulk';
  try {
    const { data } = await DetectedCampaignsAPI.bulkFormalize(
      selectionPayload.value
    );
    const [failure] = data.failures;
    useAlert(
      failure
        ? t('CAMPAIGN.DETECTED.BULK.PARTIAL', {
            count: data.formalized,
            failed: data.failures.length,
            error: failure,
          })
        : t(
            'CAMPAIGN.DETECTED.BULK.SUCCESS',
            { count: data.formalized },
            data.formalized
          )
    );
    await reloadAfterFormalize();
  } catch {
    useAlert(t('CAMPAIGN.DETECTED.CONFIRM.ERROR'));
    // Bursts before the failure may already be recorded; the list has to stop offering them.
    await reloadAfterFormalize();
  } finally {
    pending.value = null;
  }
};

const confirmDismiss = async () => {
  dismissDialogRef.value.close();
  await DetectedCampaignsAPI.dismiss(selected.value.id);
  useAlert(t('CAMPAIGN.DETECTED.DISMISS_CONFIRM.SUCCESS'));
  await fetchBursts();
};

const confirmBulkDismiss = async () => {
  bulkDismissDialogRef.value.close();
  pending.value = 'bulk';
  const count = selectedCount.value;
  try {
    await DetectedCampaignsAPI.bulkDismiss([...selectedIds.value]);
    useAlert(t('CAMPAIGN.DETECTED.BULK.DISMISS_SUCCESS', { count }, count));
    await fetchBursts();
  } finally {
    pending.value = null;
  }
};

const conversationLink = displayId =>
  frontendURL(conversationUrl({ accountId: accountId.value, id: displayId }));
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center w-full h-20">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('CAMPAIGN.DETECTED.HEADER') }}
          </span>
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl py-4 mx-auto">
        <div class="flex flex-col gap-4 pb-8">
          <div class="flex flex-col max-w-3xl gap-1">
            <p class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.DETECTED.DESCRIPTION') }}
            </p>
            <p class="text-sm text-n-slate-10">
              {{ t('CAMPAIGN.DETECTED.SAFETY_NOTE') }}
            </p>
          </div>

          <div v-if="isLoading" class="flex justify-center py-16">
            <Spinner />
          </div>

          <div
            v-else-if="!hasBursts"
            class="flex flex-col items-center gap-2 py-20 text-center"
          >
            <p class="text-n-slate-12">{{ t('CAMPAIGN.DETECTED.EMPTY') }}</p>
            <p class="max-w-lg text-sm text-n-slate-11">
              {{ t('CAMPAIGN.DETECTED.EMPTY_HINT') }}
            </p>
          </div>

          <template v-else>
            <div class="sticky top-0 z-10 py-2 bg-n-surface-1">
              <BulkSelectBar
                v-model="selectedIds"
                :all-items="bursts"
                :select-all-label="selectAllLabel"
                :selected-count-label="
                  t(
                    'CAMPAIGN.DETECTED.BULK.SELECTED',
                    { count: selectedCount },
                    selectedCount
                  )
                "
              >
                <template #default-actions>
                  <Button
                    slate
                    ghost
                    sm
                    :label="selectAllLabel"
                    @click="selectAll"
                  />
                </template>
                <template #actions>
                  <Button
                    slate
                    ghost
                    sm
                    :disabled="isBusy"
                    :label="
                      t('CAMPAIGN.DETECTED.BULK.DISMISS', {
                        count: selectedCount,
                      })
                    "
                    @click="bulkDismissDialogRef.open()"
                  />
                  <Button
                    sm
                    :is-loading="pending === 'bulk'"
                    :disabled="isBusy"
                    :label="
                      t(
                        'CAMPAIGN.DETECTED.BULK.FORMALIZE',
                        { count: selectedCount },
                        selectedCount
                      )
                    "
                    @click="bulkFormalizeDialogRef.open()"
                  />
                </template>
              </BulkSelectBar>
            </div>

            <div
              v-for="burst in bursts"
              :key="burst.id"
              class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1"
              :class="isSelected(burst.id) ? 'border-n-brand' : 'border-n-weak'"
            >
              <div class="flex items-center gap-3">
                <Checkbox
                  :model-value="isSelected(burst.id)"
                  @update:model-value="toggleSelection(burst.id)"
                />
                <InlineInput
                  v-model="titles[burst.id]"
                  :placeholder="t('CAMPAIGN.DETECTED.NAME_PLACEHOLDER')"
                  custom-input-class="font-medium"
                />
              </div>

              <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span class="font-medium text-n-slate-12 tabular-nums">
                  {{
                    t('CAMPAIGN.DETECTED.CONTACTS', { count: burst.contacts })
                  }}
                </span>
                <div class="w-px h-3 rounded-lg bg-n-strong" />
                <span class="text-n-slate-11">{{
                  inboxName(burst.inbox_id)
                }}</span>
                <div class="w-px h-3 rounded-lg bg-n-strong" />
                <span class="text-n-slate-11 tabular-nums">
                  {{
                    t('CAMPAIGN.DETECTED.PERIOD', {
                      start: formatDate(burst.started_at),
                      duration: duration(burst),
                    })
                  }}
                </span>
                <div class="w-px h-3 rounded-lg bg-n-strong" />
                <span class="text-n-slate-11 tabular-nums">
                  {{
                    t('CAMPAIGN.DETECTED.REPLY_RATE', {
                      rate: burst.reply_rate,
                    })
                  }}
                </span>
              </div>

              <p
                class="p-3 text-sm rounded-lg text-n-slate-12 bg-n-alpha-1 line-clamp-3"
              >
                {{ burst.sample }}
              </p>

              <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-xs text-n-slate-11">
                  {{
                    burst.is_template
                      ? t('CAMPAIGN.DETECTED.TEMPLATE', {
                          name: burst.template_name,
                        })
                      : senderLabel(burst)
                  }}
                </span>
                <div class="flex gap-2">
                  <Button
                    slate
                    faded
                    sm
                    :label="t('CAMPAIGN.DETECTED.VIEW_RECIPIENTS')"
                    @click="openRecipients(burst)"
                  />
                  <Button
                    slate
                    ghost
                    sm
                    :disabled="isBusy"
                    :label="t('CAMPAIGN.DETECTED.DISMISS')"
                    @click="openDismiss(burst)"
                  />
                  <Button
                    sm
                    :is-loading="pending === burst.id"
                    :disabled="isBusy"
                    :label="t('CAMPAIGN.DETECTED.FORMALIZE')"
                    @click="formalize(burst)"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>

        <Dialog
          ref="bulkFormalizeDialogRef"
          :title="
            t(
              'CAMPAIGN.DETECTED.BULK.CONFIRM.TITLE',
              { count: selectedCount },
              selectedCount
            )
          "
          :description="t('CAMPAIGN.DETECTED.BULK.CONFIRM.BODY')"
          :confirm-button-label="t('CAMPAIGN.DETECTED.BULK.CONFIRM.CONFIRM')"
          :cancel-button-label="t('CAMPAIGN.DETECTED.BULK.CONFIRM.CANCEL')"
          @confirm="confirmBulkFormalize"
        />

        <Dialog
          ref="bulkDismissDialogRef"
          type="alert"
          :title="
            t(
              'CAMPAIGN.DETECTED.BULK.DISMISS_CONFIRM.TITLE',
              { count: selectedCount },
              selectedCount
            )
          "
          :description="t('CAMPAIGN.DETECTED.BULK.DISMISS_CONFIRM.BODY')"
          :confirm-button-label="
            t('CAMPAIGN.DETECTED.BULK.DISMISS_CONFIRM.CONFIRM')
          "
          :cancel-button-label="
            t('CAMPAIGN.DETECTED.BULK.DISMISS_CONFIRM.CANCEL')
          "
          @confirm="confirmBulkDismiss"
        />

        <Dialog
          ref="dismissDialogRef"
          type="alert"
          :title="t('CAMPAIGN.DETECTED.DISMISS_CONFIRM.TITLE')"
          :description="t('CAMPAIGN.DETECTED.DISMISS_CONFIRM.BODY')"
          :confirm-button-label="t('CAMPAIGN.DETECTED.DISMISS_CONFIRM.CONFIRM')"
          :cancel-button-label="t('CAMPAIGN.DETECTED.DISMISS_CONFIRM.CANCEL')"
          @confirm="confirmDismiss"
        />

        <Dialog
          ref="recipientsDialogRef"
          width="2xl"
          :title="t('CAMPAIGN.DETECTED.RECIPIENTS.TITLE')"
          :show-confirm-button="false"
        >
          <div v-if="isLoadingRecipients" class="flex justify-center py-8">
            <Spinner />
          </div>
          <div v-else class="overflow-x-auto max-h-96">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="py-2 pr-4 font-medium">
                    {{ t('CAMPAIGN.DETECTED.RECIPIENTS.NAME') }}
                  </th>
                  <th class="py-2 pr-4 font-medium">
                    {{ t('CAMPAIGN.DETECTED.RECIPIENTS.PHONE') }}
                  </th>
                  <th class="py-2 pr-4 font-medium">
                    {{ t('CAMPAIGN.DETECTED.RECIPIENTS.SENT_AT') }}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="recipient in recipients"
                  :key="recipient.contact_id"
                  class="border-t border-n-weak"
                >
                  <td class="py-2 pr-4 text-n-slate-12">
                    {{ recipient.name }}
                  </td>
                  <td class="py-2 pr-4 text-n-slate-11 tabular-nums">
                    {{ recipient.phone_number }}
                  </td>
                  <td class="py-2 pr-4 text-n-slate-11 tabular-nums">
                    {{ formatDate(recipient.sent_at) }}
                  </td>
                  <td class="py-2">
                    <router-link
                      :to="conversationLink(recipient.conversation_display_id)"
                      class="text-n-blue-11 hover:underline"
                    >
                      {{ t('CAMPAIGN.DETECTED.RECIPIENTS.CONVERSATION') }}
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Dialog>
      </div>
    </main>
  </section>
</template>
